/**
 * Pflicht-Zubehör fehlt: Hinweis im Warenkorb, optional Rückfrage vor dem Checkout.
 *
 * Manche Produkte sind ohne ein bestimmtes Zusatzprodukt nicht nutzbar — ein Datensammler
 * ohne Portal-Paket, ein Zähler ohne Montageplatte. Die Regel steht am Produkt
 * (custom.requires_products); snippets/cart-summary.liquid prüft serverseitig, welche
 * Produkte im Warenkorb unversorgt sind, und rendert snippets/requirement-notice.liquid.
 *
 * Die reine Warnbox braucht kein JavaScript. Erst wenn mindestens eines der unversorgten
 * Produkte custom.requirement_confirm gesetzt hat, kommt data-confirm="true" am Element an —
 * dann öffnet „Zur Kasse" zuerst ein Modal, das der Kunde abhaken muss.
 *
 * Eingehängt wird am submit des Warenkorb-Formulars, NICHT am Klick des Buttons:
 * cart-consent.js fängt den Klick bereits ab und löst danach form.requestSubmit() aus —
 * ein zweiter Klick-Listener würde also übersprungen. Der submit läuft in beiden Wegen.
 *
 * Die Bestätigung wandert als Bestell-Attribut in den Warenkorb (Nachweis in der Bestellung).
 * Express-Checkout-Buttons werden bei aktivem Hinweis in cart-summary.liquid ausgeblendet,
 * weil sie am Formular vorbei direkt in den Checkout springen.
 */

const CONFIRM_FLAG = 'requirementConfirmed'; // form.dataset → data-requirement-confirmed
const ATTR_CONFIRMED = 'accessory_requirement_confirmed';
const ATTR_TIMESTAMP = 'accessory_requirement_confirmed_at';

// Warenkorbseite und Drawer rendern beide eine Zusammenfassung und hängen sich damit
// beide an dasselbe Formular. Ohne diesen Riegel öffnen zwei Instanzen zwei Modals.
let modalOpen = false;

// Warenkorbseite und Drawer bauen je einen eigenen Dialog. Die ids für aria-labelledby
// müssen im Dokument trotzdem eindeutig bleiben.
let dialogSeq = 0;

class RequirementNotice extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  dialog = null;
  /**
   * Auflöser der laufenden Rückfrage. Wird gebraucht, um ein offenes Modal beim
   * Entfernen des Elements sauber zu beenden — sonst bliebe `modalOpen` gesetzt.
   * @type {((result: boolean | null) => void) | null}
   */
  settle = null;
  /** @type {HTMLButtonElement | null} */
  checkoutButton = null;
  /** @type {HTMLFormElement | null} */
  form = null;
  updateUrl = '/cart/update.js';

  connectedCallback() {
    // Ohne Rückfrage ist die Box reine Anzeige — kein Eingriff in den Checkout.
    if (this.dataset.confirm !== 'true') return;

    this.updateUrl = this.dataset.updateUrl || '/cart/update.js';

    const scope = this.parentElement || document;
    const ctas = scope.querySelector('.cart__ctas');
    this.checkoutButton = ctas
      ? /** @type {HTMLButtonElement | null} */ (ctas.querySelector('.cart__checkout-button'))
      : null;
    this.form = this.checkoutButton ? this.checkoutButton.form : null;
    if (!this.form) return;

    // Ein frisches Rendern bedeutet: der Warenkorb hat sich geändert. Eine frühere
    // Bestätigung gilt damit nicht mehr — sonst rutscht eine geänderte Bestellung
    // ungefragt durch. Das Formular selbst überlebt das Morphing des Theme.
    delete this.form.dataset[CONFIRM_FLAG];

    this.handleSubmit = this.handleSubmit.bind(this);
    this.form.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    if (this.form && this.handleSubmit) this.form.removeEventListener('submit', this.handleSubmit);
    // Ein neues Rendern des Warenkorbs kann mitten in die offene Rückfrage fallen.
    // Die Rückfrage gilt dann als abgebrochen — würde sie nur verschwinden, bliebe
    // das Promise offen und `modalOpen` für den Rest der Seite gesetzt.
    if (this.settle) this.settle(false);
    if (this.dialog) {
      if (this.dialog.open) this.dialog.close();
      this.dialog.remove();
      this.dialog = null;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    const form = this.form;
    if (!form) return;

    // Das Warenkorb-Formular kann auch andere Submit-Buttons tragen (z. B. Mengen-Update);
    // gemeint ist nur der Weg in den Checkout.
    const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);
    if (submitter && submitter.name !== 'checkout') return;
    if (form.dataset[CONFIRM_FLAG] === 'true') return;

    event.preventDefault();
    if (modalOpen) return;
    modalOpen = true;

    this.ask().then((confirmed) => {
      modalOpen = false;
      if (confirmed === false) return;

      form.dataset[CONFIRM_FLAG] = 'true';
      const proceed = () => {
        const button = submitter || this.checkoutButton;
        if (typeof form.requestSubmit === 'function') form.requestSubmit(button || undefined);
        else form.submit();
      };

      // `null`: die Rückfrage ließ sich nicht darstellen. Dann lieber durchlassen als den
      // Kauf blockieren — aber ohne Bestätigung zu protokollieren, die niemand gegeben hat.
      if (confirmed === null) proceed();
      else this.commit().finally(proceed);
    });
  }

  /**
   * Schreibt die Bestätigung als Bestell-Attribut. keepalive: läuft auch über den
   * Seitenwechsel in den Checkout hinaus zu Ende.
   * @returns {Promise<any>}
   */
  commit() {
    /** @type {Record<string, string>} */
    const attributes = {};
    attributes[ATTR_CONFIRMED] = 'true';
    attributes[ATTR_TIMESTAMP] = new Date().toISOString();
    return fetch(this.updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
      keepalive: true,
    }).catch((error) => {
      // Der Kauf läuft trotzdem weiter — aber der Nachweis fehlt dann in der Bestellung,
      // und das soll nicht spurlos passieren.
      console.warn('[requirement-notice] Bestätigung konnte nicht gespeichert werden', error);
    });
  }

  /** @returns {HTMLDialogElement} */
  ensureDialog() {
    if (this.dialog) return this.dialog;

    dialogSeq += 1;
    const titleId = `requirement-dialog-title-${dialogSeq}`;
    const bodyId = `requirement-dialog-body-${dialogSeq}`;

    const dialog = document.createElement('dialog');
    dialog.className = 'requirement-dialog';
    // Ohne diese Verknüpfung meldet der Screenreader nur „Dialog" — der native
    // <dialog> übernimmt keine Überschrift aus seinem Inhalt.
    dialog.setAttribute('aria-labelledby', titleId);
    dialog.setAttribute('aria-describedby', bodyId);
    dialog.innerHTML = `
      <div class="requirement-dialog__inner">
        <h2 class="requirement-dialog__title" id="${titleId}">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
            <path d="M12 3.2 22 20.4H2L12 3.2Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
            <path d="M12 9.4v4.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            <circle cx="12" cy="17.2" r="1.1" fill="currentColor"/>
          </svg>
          <span data-rn-title></span>
        </h2>
        <p class="requirement-dialog__body" id="${bodyId}" data-rn-body></p>
        <label class="requirement-dialog__check">
          <input type="checkbox" class="requirement-dialog__cb" data-rn-cb>
          <span data-rn-check></span>
        </label>
        <div class="requirement-dialog__actions">
          <button type="button" class="requirement-dialog__btn requirement-dialog__btn--cancel" data-rn-cancel></button>
          <button type="button" class="requirement-dialog__btn requirement-dialog__btn--confirm" data-rn-confirm disabled></button>
        </div>
      </div>
    `;
    document.body.appendChild(dialog);
    this.dialog = dialog;
    return dialog;
  }

  /**
   * Zeigt die Rückfrage und löst zu true (weiter zur Zahlung) / false (zurück) auf.
   * `null` heißt: die Rückfrage ließ sich nicht aufbauen — der Aufrufer lässt den Kauf
   * dann durch, ohne eine Bestätigung zu protokollieren.
   * Der Weiter-Button bleibt gesperrt, bis das Häkchen gesetzt ist.
   * @returns {Promise<boolean | null>}
   */
  ask() {
    const dialog = this.ensureDialog();
    const titleEl = dialog.querySelector('[data-rn-title]');
    const bodyEl = dialog.querySelector('[data-rn-body]');
    const checkEl = dialog.querySelector('[data-rn-check]');
    const checkbox = /** @type {HTMLInputElement | null} */ (dialog.querySelector('[data-rn-cb]'));
    const confirmBtn = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-rn-confirm]'));
    const cancelBtn = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-rn-cancel]'));

    // Fehlt ein Bedienelement, wäre das Modal eine Sackgasse: der Kunde käme weder
    // weiter noch zurück. Dann lieber nicht öffnen.
    if (!checkbox || !confirmBtn || !cancelBtn) {
      console.warn('[requirement-notice] Rückfrage unvollständig, Checkout läuft ohne Bestätigung');
      return Promise.resolve(null);
    }

    if (titleEl) titleEl.textContent = this.dataset.title || 'Zubehör fehlt';
    if (bodyEl) bodyEl.textContent = this.dataset.body || '';
    if (checkEl) checkEl.textContent = this.dataset.check || '';
    confirmBtn.textContent = this.dataset.confirmLabel || 'Trotzdem fortfahren';
    cancelBtn.textContent = this.dataset.cancelLabel || 'Zurück zum Warenkorb';

    // Jede Rückfrage startet leer — eine einmal gesetzte Bestätigung soll nicht
    // für den nächsten Checkout-Versuch stehen bleiben.
    checkbox.checked = false;
    confirmBtn.disabled = true;

    return new Promise((resolve) => {
      let settled = false;
      const finish = (/** @type {boolean | null} */ result) => {
        if (settled) return;
        settled = true;
        this.settle = null;
        checkbox.removeEventListener('change', onToggle);
        confirmBtn.removeEventListener('click', onConfirm);
        cancelBtn.removeEventListener('click', onCancel);
        dialog.removeEventListener('cancel', onEscape);
        if (dialog.open) dialog.close();
        resolve(result);
      };
      const onToggle = () => {
        confirmBtn.disabled = !checkbox.checked;
      };
      const onConfirm = () => {
        if (checkbox.checked) finish(true);
      };
      const onCancel = () => finish(false);
      // ESC => zurück in den Warenkorb. Ein Backdrop-Klick schließt den nativen
      // Dialog bewusst NICHT — die Entscheidung soll ausdrücklich fallen.
      const onEscape = (/** @type {Event} */ e) => {
        e.preventDefault();
        finish(false);
      };

      checkbox.addEventListener('change', onToggle);
      confirmBtn.addEventListener('click', onConfirm);
      cancelBtn.addEventListener('click', onCancel);
      dialog.addEventListener('cancel', onEscape);

      this.settle = finish;
      dialog.showModal();
      checkbox.focus();
    });
  }
}

if (!customElements.get('requirement-notice')) {
  customElements.define('requirement-notice', RequirementNotice);
}
